<template>
  <v-menu :close-on-content-click="false" :nudge-width="250" offset-y left>
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        id="btn-notificacoes-com-alerta"
        icon
        v-bind="attrs"
        v-on="on"
        style="box-shadow: none"
        class="text-none"
        v-if="autorizacoesDados.length > 0 || (canViewAusentes && totalAusentes > 0)"
        color="white"
      >
        <v-badge
          id="badge-notificacoes-alerta"
          :content="autorizacoesDados.length + (canViewAusentes ? totalAusentes : 0)"
          color="error"
          overlap
        >
          <v-icon color="white">fa fa-bell</v-icon>
        </v-badge>
      </v-btn>
      <v-btn
        v-else
        id="btn-notificacoes-sem-alerta"
        icon
        v-bind="attrs"
        v-on="on"
        style="box-shadow: none"
        class="text-none"
      >
        <v-icon color="white" aria-hidden="true">fa fa-bell</v-icon>
      </v-btn>
    </template>

    <v-card class="mx-auto notification-card" max-width="400">
      <!-- Card Header -->
      <v-card-title class="d-flex justify-space-between align-center py-3 px-4 primary white--text">
        <span class="text-subtitle-1 font-weight-bold">Notificações</span>
        <v-chip
          v-if="totalNotificacoes > 0"
          small
          color="white"
          class="primary--text font-weight-bold"
        >
          {{ totalNotificacoes }} pendente(s)
        </v-chip>
      </v-card-title>

      <v-divider></v-divider>

      <!-- Scrollable List -->
      <v-list class="py-0" style="max-height: 350px; overflow-y: auto">
        <!-- Empty State -->
        <v-list-item
          v-if="totalNotificacoes === 0"
          class="py-8 text-center d-flex flex-column align-center justify-center"
        >
          <v-icon size="40" color="grey lighten-1" class="mb-2">mdi-bell-outline</v-icon>
          <div class="text-body-2 grey--text text--darken-1 font-weight-medium">
            Nenhuma notificação pendente
          </div>
        </v-list-item>

        <!-- Authorization Requests -->
        <template v-if="autorizacoesDados.length > 0">
          <v-list-item
            v-for="(autorizacao, i) of autorizacoesDados"
            :key="'auth-' + i"
            class="notification-item py-2 border-bottom"
            @click="pushAutorizacoes()"
            ripple
          >
            <v-list-item-avatar color="teal lighten-5" size="40">
              <v-icon color="teal">mdi-shield-lock-outline</v-icon>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title class="font-weight-bold text-body-2">
                Pedido de Autorização
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption text-truncate mt-1">
                Tipo: {{ autorizacao?.pedido?.descricao }}
              </v-list-item-subtitle>
              <v-list-item-subtitle class="text-caption">
                De: {{ autorizacao.solicitante.name }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </template>

        <!-- Absent Students Notification -->
        <v-list-item
          v-if="canViewAusentes && totalAusentes > 0"
          class="notification-item py-2"
          ripple
        >
          <v-list-item-avatar
            color="error lighten-5"
            size="40"
            class="cursor-pointer"
            @click="pushAlunosAusentes()"
          >
            <v-icon color="error">mdi-account-alert-outline</v-icon>
          </v-list-item-avatar>

          <v-list-item-content class="cursor-pointer" @click="pushAlunosAusentes()">
            <v-list-item-title class="error--text font-weight-bold text-body-2">
              Alunos Ausentes
            </v-list-item-title>
            <v-list-item-subtitle class="text-caption mt-1">
              {{ totalAusentes }} aluno(s) sem entrada por 3+ dias.
            </v-list-item-subtitle>
            <v-list-item-subtitle class="text-caption primary--text font-weight-medium">
              Clique para visualizar
            </v-list-item-subtitle>
          </v-list-item-content>

          <!-- Quick Action to Read All -->
          <v-list-item-action class="my-0">
            <v-tooltip left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="grey darken-1"
                  v-bind="attrs"
                  v-on="on"
                  @click.stop="limparNotificacoesAusentes"
                >
                  <v-icon>mdi-check-all</v-icon>
                </v-btn>
              </template>
              <span>Marcar como lidas</span>
            </v-tooltip>
          </v-list-item-action>
        </v-list-item>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "ENotificacoesList",
  computed: {
    ...mapGetters("Autorizacoes", ["autorizacoes", "autorizacoesList"]),
    ...mapGetters("Auth", ["user"]),
    canViewAusentes() {
      if (!this.user || !this.user.roles) return false;
      const allowedRoles = [
        "admin",
        "super-admin",
        "direção",
        "diretor(a)",
        "coordenador",
        "secretario",
        "secretario-escolar",
        "auxiliar-administrativo",
        "coordenador-semed",
        "direção-titular",
      ];
      return this.user.roles.some((role) => allowedRoles.includes(role.name));
    },
    totalNotificacoes() {
      return this.autorizacoesDados.length + (this.canViewAusentes ? this.totalAusentes : 0);
    },
  },
  data() {
    return {
      qtdPedidosPendentes: 0,
      autorizacoesDados: [],
      totalAusentes: 0,
    };
  },
  mounted() {
    this.loadPedidos();
  },
  methods: {
    ...mapActions("Autorizacoes", ["loadAutorizacoes"]),

    async loadPedidos() {
      try {
        this.$loaderService.open("Carregando dados");
        const promises = [this.loadAutorizacoes()];
        if (this.canViewAusentes) {
          promises.push(this.loadAusentesCount());
        }
        await Promise.all(promises);
        this.autorizacoesDados = this.autorizacoesList.filter((a) => a.status === "PENDENTE");
        this.$loaderService.close();
        this.$forceUpdate();
      } catch (error) {
        this.$handleError(error);
        this.$loaderService.close();
      }
    },
    async loadAusentesCount() {
      try {
        const response = await this.$services.presencasService.getAlunosAusentesCount();
        this.totalAusentes = response.count || 0;
      } catch (e) {
        console.error(e);
      }
    },
    async limparNotificacoesAusentes() {
      try {
        this.$loaderService.open("Marcando todas como lidas...");
        await this.$services.presencasService.marcarTodasComoLidas();
        this.totalAusentes = 0;
        this.$toast.success("Notificações marcadas como lidas.");
        this.$forceUpdate();
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.$loaderService.close();
      }
    },
    pushAutorizacoes() {
      this.$router.push({ name: "autorizacoes" });
    },
    pushAlunosAusentes() {
      this.$router.push({ name: "notifiq.alunosAusentes" });
    },
  },
};
</script>

<style scoped>
.notification-card {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15) !important;
  border-radius: 8px !important;
  overflow: hidden;
}
.notification-item {
  transition: background-color 0.2s;
}
.notification-item:hover {
  background-color: #f5f5f5;
}
.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}
.cursor-pointer {
  cursor: pointer;
}
</style>
